import { ChevronDown } from 'lucide-react';
import { useState } from "react";
import { AnimatedText, AnimatedTitle } from "@/components/ui/AnimatedText";
import LuxuryCard from "@/components/ui/LuxuryCard";
import { blogPosts, blogCategories } from "@/utils/blogData";
import { useSEO } from "@/utils/useSEO";
import { useTranslation } from "react-i18next";

const POSTS_PER_PAGE = 8;

export default function News() {
  const { t } = useTranslation();
  useSEO(
    "News & Journal",
    "Stay updated with the latest stories, travel tips, and guides from Patal Kikian Villas Sidemen. Explore articles about Bali culture, wellness, activities, and insider tips for your perfect Sidemen retreat."
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Filter posts if a category is selected
  const filteredPosts = selectedCategory
    ? blogPosts.filter((post) => post.categorySlug === selectedCategory)
    : blogPosts;

  // Calculate pagination details
  const totalPosts = filteredPosts.length;
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const scrollTarget = document.getElementById("articles-section");
    if (scrollTarget) {
      scrollTarget.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleCategorySelect = (slug: string | null) => {
    setSelectedCategory(slug);
    setCurrentPage(1);
  };

  return (
    <>
      {/* Hero Header */}
      <section className="relative min-h-[100dvh] w-full flex items-center justify-center">
        <div className="absolute inset-0">
          <img src="/media/patal-kikian-hero-banner.webp" alt="News Background" className="w-full h-full object-cover opacity-85" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        </div>
        
        <div className="relative z-10 text-center px-4 mt-20">
          <AnimatedText delay={0.2} className="text-gold uppercase tracking-[0.3em] text-sm mb-4 font-semibold">
            {t("news.journal", "Journal")}
          </AnimatedText>
          <AnimatedTitle
            title={t("news.latest", "Latest News & Stories")}
            className="text-4xl md:text-5xl lg:text-6xl font-luxury text-shadow-lg leading-tight mb-6 max-w-4xl mx-auto"
          />
        
          <AnimatedText delay={0.3}>
            <p className="text-white/80 max-w-3xl mx-auto text-lg leading-relaxed">
              {t("news.subtitle", "Stay updated with the latest news, stories, and events happening around Patal Kikian and Sidemen.")}
            </p>
          </AnimatedText>
          
        </div>
      
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <button 
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            className="text-white/70 hover:text-white transition-colors duration-300 flex flex-col items-center gap-2 cursor-pointer"
            aria-label="Scroll to content"
          >
            <span className="uppercase tracking-[0.2em] text-[10px]">{t("news.readNews", "Read News")}</span>
            <ChevronDown size={24} className="animate-bounce" />
          </button>
        </div>
      </section>

      {/* Main Content Section */}
      <section id="articles-section" className="py-20 bg-[#0B0B0B]">
        <div className="container mx-auto px-6">
          
          {/* Category Filter Pills */}
          <div className="flex flex-wrap justify-center items-center gap-3 mb-16">
            <button
              onClick={() => handleCategorySelect(null)}
              className={`px-6 py-2.5 rounded-full text-xs uppercase tracking-widest border transition-all duration-300 font-semibold ${
                selectedCategory === null
                  ? "bg-gold border-gold text-black"
                  : "bg-transparent border-white/10 text-white/70 hover:border-gold hover:text-white"
              }`}
            >
              {t("news.allArticles", "All Articles")}
            </button>
            {blogCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategorySelect(cat.slug)}
                className={`px-6 py-2.5 rounded-full text-xs uppercase tracking-widest border transition-all duration-300 font-semibold ${
                  selectedCategory === cat.slug
                    ? "bg-gold border-gold text-black"
                    : "bg-transparent border-white/10 text-white/70 hover:border-gold hover:text-white"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Grid of Articles */}
          {paginatedPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/60 text-lg">{t("news.noArticles", "No articles found in this category.")}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {paginatedPosts.map((post, index) => (
                  <LuxuryCard
                    key={post.id}
                    title={post.title}
                    image={post.image}
                    href={`/news/${post.slug}`}
                    description={post.excerpt}
                    features={[post.category, post.date]}
                    delay={index * 0.05}
                  />
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-16">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-white"
                  >
                    ←
                  </button>
                  <span className="text-sm text-white/60">
                    {t("news.page", "Page")} <strong className="text-white">{currentPage}</strong> {t("news.of", "of")} {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-white"
                  >
                    →
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </section>
    </>
  );
}
